import React, {useState} from "react";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import tiendaService from "../../../_services/cart/tienda.service";
import alertService from "../../../_services/cart/alert.service";

/**
 * "Completar con IA", arriba del primer paso del alta.
 *
 * <p>El vendedor escribe una linea libre y el back le devuelve los campos que pudo deducir, ya
 * cruzados contra el catalogo (ver AsistentePublicacionService). Esto no publica nada: solo llena el
 * formulario, que se sigue revisando paso por paso.
 *
 * <p>Si ningun tipo del catalogo corresponde, la IA propone uno nuevo y aca se ofrece agregarlo.
 * Queda pendiente de revision del admin; la publicacion lo usa igual.
 *
 * @param onAplicar   recibe la sugerencia y llena el form
 * @param onTipoNuevo recibe el tipo recien creado (pendiente) para sumarlo al combo y elegirlo
 * @param categorias  para elegir la categoria del tipo nuevo si la IA no la pudo deducir
 */
export const AsistenteIA = ({onAplicar, onTipoNuevo, categorias}) => {
    const [texto, setTexto] = useState("");
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [creandoTipo, setCreandoTipo] = useState(false);
    const [categoriaTipo, setCategoriaTipo] = useState(null);

    const completar = () => {
        if (cargando || texto.trim().length < 5) return;
        setCargando(true);
        tiendaService.iaSugerir(texto)
            .then((s) => {
                setResultado(s);
                setCategoriaTipo(s?.tipoNuevo?.categoriaId ?? null);
                onAplicar(s);
            })
            .catch((err) => alertService.error(err))
            .finally(() => setCargando(false));
    };

    const agregarTipo = () => {
        const propuesto = resultado?.tipoNuevo;
        if (!propuesto || !categoriaTipo) return;
        setCreandoTipo(true);
        tiendaService.iaCrearTipo(propuesto.nombre, categoriaTipo, texto)
            .then((tipo) => {
                onTipoNuevo(tipo);
                setResultado({...resultado, tipoNuevo: null, tipoRepuesto: tipo});
            })
            .catch((err) => alertService.error(err))
            .finally(() => setCreandoTipo(false));
    };

    const completados = resultado ? [
        resultado.tipoRepuesto && "Tipo: " + resultado.tipoRepuesto.nombre + (resultado.tipoRepuesto.pendiente ? " (en revisión)" : ""),
        resultado.marca && "Vehículo: " + [resultado.marca, resultado.modelo, resultado.version]
            .filter(Boolean).map((x) => x.nombre).join(" "),
        resultado.anio && "Año: " + resultado.anio,
        resultado.precio && "Precio: $ " + Number(resultado.precio).toLocaleString("es-AR"),
        resultado.cantidad && "Cantidad: " + resultado.cantidad,
        resultado.descripcion && "Descripción",
    ].filter(Boolean) : [];

    const propuesto = resultado?.tipoNuevo;

    return (
        <section className="asistente-ia">
            <div className="asistente-ia__cabecera">
                <FontAwesomeIcon icon="fa-solid fa-wand-magic-sparkles"/>
                <strong>Completar con IA</strong>
            </div>
            <p className="asistente-ia__ayuda">
                Escribí lo que publicás como se lo contarías a un cliente: la pieza, el auto, el año, el
                estado y el precio. Completamos el formulario y vos lo revisás antes de publicar.
            </p>

            <div className="asistente-ia__entrada">
                <textarea
                    rows={2}
                    maxLength={1000}
                    value={texto}
                    placeholder="Ej: bomba de agua Gol Trend 1.6 2015, nueva en caja, $45 mil"
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) completar();
                    }}
                />
                <button
                    type="button"
                    className="button"
                    data-type="primary-small"
                    disabled={cargando || texto.trim().length < 5}
                    onClick={completar}
                >
                    {cargando ? "Completando…" : "Completar"}
                </button>
            </div>

            {resultado && (
                <div className="asistente-ia__resultado" aria-live="polite">
                    {completados.length > 0 && (
                        <ul>
                            {completados.map((c) => (
                                <li key={c} data-tipo="ok">
                                    <FontAwesomeIcon icon="fa-solid fa-check"/> {c}
                                </li>
                            ))}
                        </ul>
                    )}
                    {(resultado.avisos || []).length > 0 && (
                        <ul>
                            {resultado.avisos.map((a) => (
                                <li key={a} data-tipo="aviso">
                                    <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation"/> {a}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {propuesto && (
                <div className="asistente-ia__tipo-nuevo">
                    <p>
                        No tenemos <strong>«{propuesto.nombre}»</strong> en el catálogo. Lo podemos agregar:
                        queda en revisión y tu publicación sale igual. Si preferís, elegí un tipo parecido de la lista.
                    </p>
                    <div className="asistente-ia__entrada">
                        <Select
                            className="asistente-ia__categoria"
                            placeholder="Categoría"
                            value={(categorias || []).find((c) => c.id === categoriaTipo) || null}
                            options={categorias || []}
                            getOptionLabel={(c) => c.nombre}
                            getOptionValue={(c) => c.id}
                            onChange={(c) => setCategoriaTipo(c ? c.id : null)}
                        />
                        <button
                            type="button"
                            className="button"
                            data-type="complementary-small"
                            disabled={creandoTipo || !categoriaTipo}
                            onClick={agregarTipo}
                        >
                            {creandoTipo ? "Agregando…" : "Agregar «" + propuesto.nombre + "»"}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};
